import { useCallback, useMemo, useRef } from 'react';
import { useT, type MessageKey } from '../lib/i18n';
import type { EdgeKind, TaxGraph, TaxNode } from '../lib/taxonomyGraph';
import { Wires, type Wire } from './Wires';
import { KindIcon } from './icons';

interface Side { key: string; edge: EdgeKind; nodes: { node: TaxNode; label?: string }[] }

/** In which order the kinds of line are listed on each side. */
const ORDER: EdgeKind[] = ['extends', 'flow', 'applies', 'refers', 'holds', 'link', 'uses', 'opens'];

/**
 * One part of the taxonomy and what it touches (docs/en/43 sec.8): on the left what it stands
 * on - the template it extends, what its fields refer to, the template a directive is about - and
 * on the right what stands on it - the templates below it, what refers to it, the directives about
 * it, by kind. However big the taxonomy, this is the size of one part's neighbourhood; going to a
 * neighbour is one click, and back is the trail above.
 */
export function TaxonomyFocus({ graph, selected, trail, onSelect, onBack, summary, actions }: {
  graph: TaxGraph;
  selected: string;
  /** The parts looked at before this one, oldest first. */
  trail: string[];
  onSelect: (id: string) => void;
  onBack: (index: number) => void;
  /** What the chosen part is, in a few words: its fields, its states, its effects. */
  summary: string[];
  /** Buttons for the chosen part: open the flow designer, edit. */
  actions?: React.ReactNode;
}) {
  const { t } = useT();
  const root = useRef<HTMLDivElement>(null);
  const node = graph.byId.get(selected);

  const [left, right] = useMemo(() => {
    const out: Side[] = [];
    const inn: Side[] = [];
    for (const kind of ORDER) {
      const outs = graph.edges.filter((e) => e.kind === kind && e.from === selected)
        .map((e) => ({ node: graph.byId.get(e.to)!, label: e.label })).filter((x) => x.node);
      if (outs.length > 0) out.push({ key: `out-${kind}`, edge: kind, nodes: outs });
      const ins = graph.edges.filter((e) => e.kind === kind && e.to === selected)
        .map((e) => ({ node: graph.byId.get(e.from)!, label: e.label })).filter((x) => x.node);
      if (ins.length === 0) continue;
      // The directives about a template, a group per kind: "3 actions, 2 views" reads better than 30 boxes.
      if (kind === 'applies' || kind === 'uses') {
        const byKind = new Map<string, { node: TaxNode; label?: string }[]>();
        for (const x of ins) byKind.set(x.node.kind, [...(byKind.get(x.node.kind) ?? []), x]);
        for (const [k, nodes] of byKind) inn.push({ key: `in-${kind}-${k}`, edge: kind, nodes: nodes.sort((a, b) => a.node.label.localeCompare(b.node.label)) });
      } else {
        inn.push({ key: `in-${kind}`, edge: kind, nodes: ins.sort((a, b) => a.node.label.localeCompare(b.node.label)) });
      }
    }
    return [out, inn];
  }, [graph, selected]);

  const wires = useMemo(() => {
    const w: Wire[] = [];
    for (const side of left) for (const x of side.nodes) w.push({ from: 'focus:center', to: `focus:${side.key}:${x.node.id}`, kind: side.edge, strong: true });
    for (const side of right) for (const x of side.nodes) w.push({ from: `focus:${side.key}:${x.node.id}`, to: 'focus:center', kind: side.edge, strong: true });
    return w;
  }, [left, right]);

  const anchor = useCallback((id: string) => root.current?.querySelector(`[data-anchor="${CSS.escape(id)}"]`) ?? null, []);

  if (!node) return <div className="empty">{t('taxonomy.gone')}</div>;

  const column = (sides: Side[], where: 'out' | 'in') => (
    <div className={`tx-focus-col ${where}`}>
      <h4 className="tx-focus-col-title">{t(where === 'out' ? 'tx.focus.standsOn' : 'tx.focus.usedBy')}</h4>
      {sides.length === 0 && <p className="hint">{t(where === 'out' ? 'tx.focus.nothingBelow' : 'tx.focus.nothingAbove')}</p>}
      {sides.map((side) => (
        <div key={side.key} className="tx-focus-side">
          <div className="tx-focus-side-head">
            {(side.edge === 'applies' || side.edge === 'uses') && where === 'in'
              ? <><KindIcon kind={side.nodes[0].node.kind} /> {t(`taxonomy.kinds.${side.nodes[0].node.kind === 'template' ? 'templates' : side.nodes[0].node.kind}` as MessageKey)}</>
              : t(`tx.edge.${where}.${side.edge}` as MessageKey)}
            <span className="tx-count">{side.nodes.length}</span>
          </div>
          {side.nodes.slice(0, 40).map((x) => (
            <button key={x.node.id + (x.label ?? '')} className={`tx-focus-node tx-lane-${x.node.lane}`}
                    data-anchor={`focus:${side.key}:${x.node.id}`} data-testid={`tx-focus-${x.node.id}`}
                    onClick={() => onSelect(x.node.id)} title={x.node.key}>
              <KindIcon kind={x.node.kind === 'template' ? x.node.lane : x.node.kind} />
              <span className="tx-node-label">{x.node.label}</span>
              {x.label && <span className="tx-focus-via">{x.label}</span>}
            </button>
          ))}
          {side.nodes.length > 40 && <p className="hint">{t('tx.focus.more', { count: side.nodes.length - 40 })}</p>}
        </div>
      ))}
    </div>
  );

  return (
    <div className="tx-focus" data-testid="taxonomy-focus">
      {trail.length > 0 && (
        <nav className="tx-trail" aria-label={t('tx.focus.trail')}>
          {trail.map((id, i) => {
            const n = graph.byId.get(id);
            return n ? <button key={`${id}-${i}`} className="link" onClick={() => onBack(i)}>{n.label}</button> : null;
          })}
          <span className="tx-trail-here">{node.label}</span>
        </nav>
      )}
      <div className="tx-focus-grid" ref={root}>
        {column(left, 'out')}
        <div className="tx-focus-center">
          <div className={`tx-focus-card tx-lane-${node.lane}`} data-anchor="focus:center">
            <div className="tx-focus-kind"><KindIcon kind={node.kind === 'template' ? node.lane : node.kind} size={16} />
              {node.kind === 'template' ? t(`taxonomy.lane.${node.lane}` as MessageKey) : t(`taxonomy.kind.${node.kind}` as MessageKey)}
              {node.isAbstract && <span className="chip small">{t('tx.abstract')}</span>}
            </div>
            <h3>{node.label}</h3>
            <code>{node.key}</code>
            {summary.length > 0 && <ul className="tx-focus-summary">{summary.map((s) => <li key={s}>{s}</li>)}</ul>}
            <div className="tx-focus-meta"><span className="tx-pack-tag">{node.pack}</span></div>
            {actions && <div className="tx-focus-actions">{actions}</div>}
          </div>
        </div>
        {column(right, 'in')}
        <Wires root={root} wires={wires} anchor={anchor} watch={`${selected}:${left.length}:${right.length}`} />
      </div>
    </div>
  );
}
